import { useEffect, useRef, useState } from 'react';
import { api } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Switch } from '../../components/ui/switch';
import { Loader2, BellRing, Save, Sunrise } from 'lucide-react';
import { toast } from 'sonner';

const ROLES = [
  { id: 'admin', label: 'Admin' },
  { id: 'territory_manager', label: 'TM' },
  { id: 'capturer', label: 'Capturador' },
  { id: 'agent', label: 'Agente' },
];

// Categorías y a qué roles aplica cada una (según quién las recibe hoy)
const CATEGORIES = [
  { id: 'diferenciales', label: 'Diferenciales de precio', desc: 'Ventana consolidada al cerrar un tracking con diferencias en Auto', roles: ['admin', 'territory_manager'] },
  { id: 'tracking_incompleto', label: 'Tracking incompleto', desc: 'La ronda pasó 1 hora sin completar todos los markers (se envía lo capturado)', roles: ['admin', 'territory_manager'] },
  { id: 'anomalias', label: 'Anomalías GPS', desc: 'Resumen por ronda de capturas fuera del radio o sin GPS', roles: ['admin', 'territory_manager'] },
  { id: 'pushes', label: 'Push de precios', desc: 'Nuevo cambio enviado a la estación y escalaciones', roles: ['territory_manager', 'capturer', 'agent'] },
  { id: 'confirmaciones', label: 'Confirmaciones', desc: 'El capturador confirmó el cambio con foto del monolito', roles: ['admin'] },
  { id: 'capturas', label: 'Capturas aprobadas/rechazadas', desc: 'Resultado de revisión de capturas y trackings enviados', roles: ['capturer', 'territory_manager'] },
  { id: 'recordatorios', label: 'Recordatorios de captura', desc: 'Captura pendiente o estación sin captura según horario', roles: ['capturer', 'territory_manager'] },
  { id: 'resumen_semanal', label: 'Resumen semanal', desc: 'Totales de la semana', roles: ['admin'] },
  { id: 'cuentas', label: 'Solicitudes de cuenta', desc: 'Nuevos registros de usuarios', roles: ['admin'] },
  { id: 'alerta_marker', label: 'Alerta por marker individual', desc: 'Una alerta por cada marker que cambia precio (además requiere umbral > 0 en Parámetros)', roles: ['admin'] },
];

export default function AdminNotificationCenter() {
  const [prefs, setPrefs] = useState({});
  const [digest, setDigest] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/admin/notification-settings').then(r => {
      setPrefs(r.data.prefs || {});
      setDigest(!!r.data.morning_digest_enabled);
    }).finally(() => setLoading(false));
  }, []);

  const isOn = (cat, role) => (prefs[cat]?.[role]) !== false;
  const saveTimer = useRef(null);
  const autoSave = (nextPrefs, nextDigest) => {
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      try {
        await api.put('/admin/notification-settings', { prefs: nextPrefs, morning_digest_enabled: nextDigest });
        toast.success('Guardado', { duration: 1500 });
      } catch { toast.error('No se pudo guardar el cambio — revisa tu conexión'); }
    }, 600);
  };
  const toggle = (cat, role) => setPrefs(p => {
    const next = { ...p, [cat]: { ...(p[cat] || {}), [role]: !isOn(cat, role) } };
    autoSave(next, digest);
    return next;
  });
  const toggleDigest = (v) => { setDigest(v); autoSave(prefs, v); };

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/admin/notification-settings', { prefs, morning_digest_enabled: digest });
      toast.success('Preferencias de notificaciones guardadas');
    } catch {
      toast.error('No se pudo guardar');
    } finally { setSaving(false); }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-[#8a8579]"/></div>;

  return (
    <div className="space-y-6 max-w-4xl" data-testid="notification-center-page">
      <div>
        <h2 className="font-serif text-[36px] leading-tight tracking-tight inline-flex items-center gap-3"><BellRing size={26} strokeWidth={1.5}/> Centro de notificaciones</h2>
        <p className="text-[13px] text-[#5a5a5a] mt-1">Activa o desactiva cada tipo de notificación por rol. Aplica a la campanita y a los push del teléfono. Los cambios se guardan automáticamente al tocar cada switch.</p>
      </div>

      <div className="bg-white border border-[#e8e5de] rounded-xl overflow-hidden">
        <div className="grid grid-cols-[1fr_repeat(4,86px)] gap-2 px-4 py-2.5 bg-[#f8f6f1] border-b border-[#e8e5de] text-[10.5px] uppercase tracking-wider text-[#8a8579]">
          <div>Tipo de notificación</div>
          {ROLES.map(r => <div key={r.id} className="text-center">{r.label}</div>)}
        </div>
        <div className="divide-y divide-[#f0ede5]">
          {CATEGORIES.map(cat => (
            <div key={cat.id} className="grid grid-cols-[1fr_repeat(4,86px)] gap-2 px-4 py-3 items-center" data-testid={`notif-cat-${cat.id}`}>
              <div className="min-w-0 pr-2">
                <p className="text-[13px] font-medium">{cat.label}</p>
                <p className="text-[11px] text-[#8a8579]">{cat.desc}</p>
              </div>
              {ROLES.map(r => (
                <div key={r.id} className="flex justify-center">
                  <Switch
                    checked={isOn(cat.id, r.id)}
                    onCheckedChange={() => toggle(cat.id, r.id)}
                    data-testid={`notif-${cat.id}-${r.id}`}
                  />
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white border border-[#e8e5de] rounded-xl p-4 flex items-start justify-between gap-4" data-testid="digest-card">
        <div>
          <p className="text-[13px] font-medium inline-flex items-center gap-2"><Sunrise size={15} className="text-[#d97706]"/> Resumen matutino</p>
          <p className="text-[11.5px] text-[#5a5a5a] mt-0.5 max-w-lg">Al terminar el horario silencioso (Parámetros), cada usuario con notificaciones activadas recibe UN solo push con lo acumulado durante la noche. Requiere horario silencioso activado.</p>
        </div>
        <Switch checked={digest} onCheckedChange={toggleDigest} data-testid="digest-toggle"/>
      </div>

      <div className="flex justify-end">
        <Button onClick={save} disabled={saving} className="h-10 px-6 rounded-full bg-[#1c1c1c] hover:bg-black text-[#f8f6f1] text-[12.5px]" data-testid="notif-save-btn">
          {saving ? <Loader2 size={14} className="animate-spin mr-1.5"/> : <Save size={14} className="mr-1.5"/>}
          Guardar cambios
        </Button>
      </div>
    </div>
  );
}
